/**
 * The permissions in awork
 */
import type { Project } from '@awork/features/project/models/project.model'

export enum Features {
  ProjectMaster = 1,
  ProjectPlanning = 2,
  ProjectManage = 3,
  UserMaster = 4,
  UserManage = 5,
  FilesMaster = 6,
  WorkspaceMaster = 7,
  WorkspaceManager = 8,
  CompanyMaster = 9,
  CompanyManage = 10,
  ProjectTimetracking = 11,
  UserTimetracking = 12,
  TaskManage = 13,
  ProjectCreate = 14,
  ProjectOwnTeam = 15,
  UserOwnTeam = 16,
  UserPlanning = 18,
  ProjectDocumentData = 19,
  AiAgentAccess = 20,
  AiThreadAccess = 21,
  ProjectBudget = 22
}

export enum AccessLevels {
  Manage = 'manage',
  Read = 'read',
  None = 'none'
}

/** Generic feature/access requirement used by routes and navigation entries. */
export interface RequiredWorkspacePermission {
  feature: Features | Features[]
  access: AccessLevels
  additionalPermissions?: RequiredWorkspacePermission[]
}

export const TAG_MANAGEMENT_FEATURES: Features[] = [
  Features.ProjectMaster,
  Features.ProjectPlanning,
  Features.CompanyMaster,
  Features.UserMaster
]

export const TAG_MANAGEMENT_REQUIRED_PERMISSION: RequiredWorkspacePermission = {
  feature: TAG_MANAGEMENT_FEATURES,
  access: AccessLevels.Manage,
  additionalPermissions: [
    {
      feature: Features.WorkspaceManager,
      access: AccessLevels.Manage
    }
  ]
}

/**
 * Stable page identifiers persisted in role menu visibility settings.
 */
export enum PagePermissionId {
  TeamPlanner = 'team-planner',
  TimeTrackingMyDay = 'time-tracking-my-day',
  TimeTrackingControlling = 'time-tracking-controlling'
}

/**
 * Validates if an AccessLevel meets the target AccessLevel minimum requirements
 * @param {AccessLevels} value
 * @param {AccessLevels} target
 * @returns {boolean}
 */
export function hasAccessLevelOrMore(value: AccessLevels, target: AccessLevels): boolean {
  switch (target) {
    case AccessLevels.Manage:
      return value === AccessLevels.Manage
    case AccessLevels.Read:
      return value === AccessLevels.Manage || value === AccessLevels.Read
    default:
      return false
  }
}

export class Permissions {
  id: string
  feature: string
  accessLevels: AccessLevels[]

  constructor(permission: Partial<Permissions>) {
    this.id = permission.id
    this.feature = permission.feature
    this.accessLevels = permission.accessLevels
  }

  hasAnyAccessLevel(accessLevels: AccessLevels[]): boolean {
    return accessLevels.some(a => this.accessLevels.includes(a))
  }
}

export class ProjectRolePermissions {
  projectRoleId: string
  permissions: Permissions[]

  constructor(projectRolePermissions: Partial<ProjectRolePermissions>) {
    this.projectRoleId = projectRolePermissions.projectRoleId
    this.permissions = projectRolePermissions.permissions
  }
}

export interface PermissionCheck {
  features: Features | Features[]
  accessLevel: AccessLevels
  project?: Project
}

export const defaultAccessLevelProjectRole = AccessLevels.Manage

export class UserPermissions {
  /** Whether the user has the AI product. */
  aiProduct: boolean
  /** Whether the user has the PM product. */
  pmProduct: boolean
  /** Whether Project times also protects budgets and planned effort (signed rollout switch). */
  bpe?: boolean
  isAiPlanActive: boolean
  userId: string
  roleId: string
  isAdmin: boolean
  isGuest: boolean
  isBlocked: boolean
  permissions: Permissions[]
  projectRolesPermissions: ProjectRolePermissions[]
  deactivatedMenuItems: string[]
  signature: string
  userPermission?: UserPermissions

  /**
   * Maps permissions from the API and treats a missing rollout flag as disabled.
   * @param {Partial<UserPermissions>} userPermissions - Permissions from the API.
   */
  constructor(userPermissions: Partial<UserPermissions>) {
    this.aiProduct = userPermissions.aiProduct ?? true
    this.pmProduct = userPermissions.pmProduct ?? true
    this.bpe = userPermissions.bpe === true
    this.isAiPlanActive = userPermissions.isAiPlanActive ?? false
    this.userId = userPermissions.userId
    this.roleId = userPermissions.roleId
    this.isAdmin = userPermissions.isAdmin
    this.isGuest = userPermissions.isGuest
    this.isBlocked = userPermissions.isBlocked
    this.permissions = userPermissions.permissions
    this.projectRolesPermissions = userPermissions.projectRolesPermissions
    this.deactivatedMenuItems = userPermissions.deactivatedMenuItems
    this.signature = userPermissions.signature
    this.userPermission = userPermissions.userPermission
  }

  /**
   * Returns if the menuitem should be visible or not
   * @param {string} menuitemId
   * @returns {boolean}
   */
  isAllowedToSeeMenuItem(menuitemId: string): boolean {
    return !this.deactivatedMenuItems.includes(menuitemId)
  }
}

export class Feature {
  id: number
  value: string
  name: string
  description: string

  constructor(feature: Partial<Feature>) {
    this.id = feature.id
    this.value = feature.value
    this.name = feature.name
    this.description = feature.description
  }

  static get features(): Feature[] {
    return [
      {
        id: 1,
        value: 'project-master-data',
        name: 'Project Details',
        description: 'Manage projects'
      },
      {
        id: 2,
        value: 'project-planning-data',
        name: 'Project Tasks',
        description: 'Manage the project tasks'
      },
      {
        id: 3,
        value: 'project-manage-config',
        name: 'Project Config',
        description: 'Configure and manage the project types'
      },
      {
        id: 4,
        value: 'user-master-data',
        name: 'User Details',
        description: 'Manage the user information'
      },
      {
        id: 5,
        value: 'user-mange-config',
        name: 'User Config',
        description: '(currently not available)'
      },
      {
        id: 6,
        value: 'files-master-data',
        name: 'Files Details',
        description: '(currently not available)'
      },
      {
        id: 7,
        value: 'workspace-master-data',
        name: 'Workspace Details',
        description: '(currently not available)'
      },
      {
        id: 8,
        value: 'workspace-manage-config',
        name: 'Workspace Config',
        description: 'Manage the workspace settings'
      },
      {
        id: 9,
        value: 'company-master-data',
        name: 'Company Details',
        description: 'Manage companies'
      },
      {
        id: 10,
        value: 'company-manage-config',
        name: 'Company Config',
        description: 'Manage company types and the number schema'
      },
      {
        id: 11,
        value: 'project-timetracking',
        name: 'Project Time Tracking',
        description: 'Manage time Trackings of projects'
      },
      {
        id: 12,
        value: 'user-timetracking',
        name: 'Users Timetrackings',
        description: 'Manage the timetrackings of other users'
      },
      {
        id: 13,
        value: 'task-manage-config',
        name: 'Task Config',
        description: 'Configure bundles and types'
      },
      {
        id: 14,
        value: 'project-create-permission',
        name: 'Create Project',
        description: 'Create projects'
      },
      {
        id: 15,
        value: 'project-teams-data',
        name: 'Own team projects only',
        description: 'Acess to own team projects only'
      },
      {
        id: 16,
        value: 'user-teams-data',
        name: 'Own team users only',
        description: 'Acess to own team users only'
      },
      {
        id: 17,
        value: 'team-planner',
        name: 'Team planner',
        description: 'Manages team planner'
      },
      {
        id: 18,
        value: 'user-planning-data',
        name: 'Planning',
        description: 'Manages user planning data'
      },
      {
        id: 19,
        value: 'project-document-data',
        name: 'Project Docs',
        description: 'Manages project docs'
      },
      {
        id: 20,
        value: 'ai-agent-access',
        name: 'awork Agent & Custom Agents',
        description: 'Controls awork Agent and Custom Agent use, and Agent library contribution'
      },
      {
        id: 21,
        value: 'ai-thread-access',
        name: 'Project AI threads',
        description: 'Allows users to see AI threads in otherwise accessible projects'
      }
    ].map(feature => new Feature(feature))
  }

  static get projectFeatures(): Feature[] {
    return Feature.features.filter(
      feature =>
        feature.value === 'project-master-data' ||
        feature.value === 'project-planning-data' ||
        feature.value === 'project-timetracking' ||
        feature.value === 'project-document-data' ||
        feature.value === 'ai-thread-access'
    )
  }

  /**
   * Returns the needed API string for the Feature enum
   * @param {Features} featureEnum
   * @returns {string}
   */
  static getFeatureStringByEnum(featureEnum: Features): string {
    const feature = this.features.find(feat => feat.id === Number(featureEnum))

    if (feature) {
      return feature.value
    } else {
      return ''
    }
  }
}
